import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Chip, TextField, IconButton, InputAdornment, Paper, Slider, CircularProgress } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import BusinessIcon from '@material-ui/icons/Business';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ChatMessage from './ChatMessage';
import agentApi from '../../services/agentApi';

const ChatWorkspace = ({ messages = [], isAnalyzing, analysisProgress, onParameterSelect, onRunAnalysis, onSendMessage, onViewDetails, showInput = true }) => {
    const messagesEndRef = useRef(null);
    const lastMessageRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [selectedSliderValue, setSelectedSliderValue] = useState(50);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const scrollToLastMessageTop = () => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        const lastMessage = messages[messages.length - 1];
        if (isAnalyzing) {
            scrollToBottom();
        } else if (lastMessage && lastMessage.role === 'assistant') {
            scrollToLastMessageTop();
        } else {
            scrollToBottom();
        }
    }, [messages, isAnalyzing]);

    const handleSend = () => {
        if (inputValue.trim() && onSendMessage) {
            onSendMessage(inputValue);
            setInputValue('');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const CompanySearch = ({ param, onSelect, isAnalyzing }) => {
        const [open, setOpen] = useState(false);
        const [options, setOptions] = useState(param.values || []);
        const [loading, setLoading] = useState(false);
        const [inputValue, setInputValue] = useState('');

        useEffect(() => {
            if (inputValue.length < 2) {
                setOptions(param.values || []);
                return;
            }

            const timer = setTimeout(async () => {
                setLoading(true);
                try {
                    const resp = await agentApi.getAgenticCompanies(inputValue);
                    const companies = resp.data?.companies || [];
                    setOptions(companies.map(c => ({ 
                        label: c.company_name, 
                        value: c.company_name,
                        industry_name: c.industry_name,
                        full: c
                    })));
                } catch (err) {
                    console.error("Search failed", err);
                } finally {
                    setLoading(false);
                }
            }, 500);

            return () => clearTimeout(timer);
        }, [inputValue, param.values]);

        return (
            <Box width="100%" maxWidth="500px" p={2.5} bgcolor="#f8fafc" borderRadius="20px" border="1px solid #e2e8f0" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <Typography variant="caption" style={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BusinessIcon style={{ fontSize: '14px' }} />
                    Select Enterprise Entity
                </Typography>
                <Autocomplete
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    getOptionSelected={(option, value) => option.value === value.value}
                    getOptionLabel={(option) => option.label || ""}
                    options={options}
                    loading={loading}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            onSelect(param, newValue.value, newValue.full); 
                        }
                    }}
                    onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    disabled={isAnalyzing}
                    disablePortal
                    PopperProps={{
                        placement: 'bottom-start',
                        style: { paddingTop: 8 },
                        modifiers: {
                            flip: {
                                enabled: false
                            }
                        }
                    }}
                    classes={{
                        paper: 'custom-autocomplete-paper',
                        option: 'custom-autocomplete-option'
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Type min 2 characters to search..."
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ color: '#3b82f6', fontSize: '20px', marginLeft: '4px' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <React.Fragment>
                                        {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                                style: {
                                    borderRadius: '12px',
                                    backgroundColor: '#fff',
                                    fontSize: '14px',
                                    paddingLeft: '8px',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                                }
                            }}
                        />
                    )}
                    renderOption={(option) => (
                        <Box display="flex" alignItems="center" py={1}>
                            <Box 
                                width={32} 
                                height={32} 
                                borderRadius="8px" 
                                bgcolor="#eff6ff" 
                                display="flex" 
                                justifyContent="center" 
                                alignItems="center" 
                                mr={2}
                            >
                                <BusinessIcon style={{ color: '#3b82f6', fontSize: '18px' }} />
                            </Box>
                            <Box>
                                <Typography variant="body2" style={{ fontWeight: 700, color: '#1e293b' }}>{option.label}</Typography>
                                <Typography variant="caption" style={{ color: '#64748b' }}>{option.industry_name || 'Global Industry'}</Typography>
                            </Box>
                        </Box>
                    )}
                />
            </Box>
        );
    };

    return (
        <Box display="flex" flexDirection="column" height="100%" bgcolor="#fff" p={0} width="100%" overflow="hidden">
            <style>
                {`
                @keyframes shine {
                    0% { transform: scale(1); filter: drop-shadow(0 0 0px #1e88e5); }
                    50% { transform: scale(1.25); filter: drop-shadow(0 0 12px #1e88e5) brightness(1.3); }
                    100% { transform: scale(1); filter: drop-shadow(0 0 0px #1e88e5); }
                }
                .shiny-star {
                    display: inline-block;
                    animation: shine 1.5s infinite ease-in-out;
                    line-height: 1;
                }
                .chat-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .chat-scrollbar::-webkit-scrollbar-thumb {
                    background-color: #e2e8f0;
                    border-radius: 10px;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .step-spinner {
                    animation: spin 1s linear infinite;
                }
                .custom-autocomplete-paper {
                    border-radius: 16px !important;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1) !important;
                    border: 1px solid #e2e8f0 !important;
                }
                .custom-autocomplete-option {
                    padding: 8px 16px !important;
                    transition: background 0.2s !important;
                }
                .custom-autocomplete-option[aria-selected="true"] {
                    background-color: #eff6ff !important;
                }
                `}
            </style>

            <Box flex={1} overflow="auto" px={4} py={2} className="custom-scroll">
                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '20px', color: '#1a237e', marginTop: '10px' }}>
                    How can the Agents assist you today?
                </Typography>

                {messages.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px" color="textSecondary">
                        Initializing agent session...
                    </Box>
                ) : (
                    messages.map((msg, index) => {
                        const isAssistant = msg.role === 'assistant';

                        return (
                            <Box key={index} mb={3} ref={index === messages.length - 1 ? lastMessageRef : null}>
                                <ChatMessage message={msg} onViewDetails={onViewDetails} />

                                {/* Conversational "Pills" for parameter selection */}
                                {isAssistant && msg.isQuestion && (
                                    <Box mt={2} ml={7} display="flex" flexDirection="column" gap={2}>
                                        {msg.param.param_name === 'Score Range' ? (
                                            <Box p={3} bgcolor="#f8fafc" borderRadius="20px" border="1px solid #e2e8f0" display="flex" flexDirection="column" gap={3}>
                                                <Box>
                                                    <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#1e293b', display: 'block', marginBottom: '4px' }}>
                                                        Target Score Simulation
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#64748b' }}>
                                                        Selected Range: {selectedSliderValue || 50}%
                                                    </Typography>
                                                </Box>
                                                
                                                <Box px={1}>
                                                    <Slider
                                                        defaultValue={50}
                                                        step={1}
                                                        min={1}
                                                        max={100}
                                                        valueLabelDisplay="auto"
                                                        onChange={(e, val) => setSelectedSliderValue(val)}
                                                        disabled={isAnalyzing}
                                                    />
                                                    <Box display="flex" justifyContent="space-between" mt={1}>
                                                        <Typography variant="caption" color="textSecondary">1%</Typography>
                                                        <Typography variant="caption" color="textSecondary">100%</Typography>
                                                    </Box>
                                                </Box>

                                                <Button
                                                    variant="contained"
                                                    fullWidth
                                                    onClick={() => onParameterSelect(msg.param, selectedSliderValue || 50)}
                                                    disabled={isAnalyzing}
                                                    style={{
                                                        backgroundColor: '#0f172a',
                                                        color: '#fff',
                                                        borderRadius: '12px',
                                                        padding: '12px',
                                                        fontWeight: 700,
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    Confirm Score & Continue
                                                </Button>
                                            </Box>
                                        ) : msg.param.isEntityParam ? (
                                            <CompanySearch 
                                                param={msg.param} 
                                                onSelect={onParameterSelect} 
                                                isAnalyzing={isAnalyzing} 
                                            />
                                        ) : (
                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                {(msg.param.values || []).map((v) => (
                                                    <Chip
                                                        key={v.value}
                                                        label={v.label}
                                                        clickable
                                                        disabled={isAnalyzing}
                                                        onClick={() => onParameterSelect(msg.param, v.value)}
                                                        style={{
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #1e88e5',
                                                            color: '#1e88e5',
                                                            fontWeight: 500,
                                                            transition: 'all 0.2s',
                                                            marginRight: '8px',
                                                            marginBottom: '8px',
                                                        }}
                                                    />
                                                ))}
                                                {msg.param.param_name === 'Risk / Opportunity' && (
                                                    <Chip
                                                        label="Skip Selection"
                                                        clickable
                                                        disabled={isAnalyzing}
                                                        onClick={() => onParameterSelect(msg.param, 'Skipped')}
                                                        style={{
                                                            backgroundColor: '#f8fafc',
                                                            border: '1px dashed #94a3b8',
                                                            color: '#64748b',
                                                            fontWeight: 500,
                                                            marginRight: '8px',
                                                            marginBottom: '8px',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {isAssistant && msg.isReady && (
                                    <Box mt={2} ml={7}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={onRunAnalysis}
                                            disabled={isAnalyzing}
                                            style={{
                                                backgroundColor: '#1e88e5',
                                                color: '#fff',
                                                textTransform: 'none',
                                                borderRadius: '24px',
                                                padding: '10px 32px',
                                                fontWeight: 600,
                                                boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)'
                                            }}
                                        >
                                            {isAnalyzing && <span className="shiny-star" style={{ color: '#fff', marginRight: 10, fontSize: 18 }}>✦</span>}
                                            {isAnalyzing ? 'Crafting Response...' : 'Run Analysis'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        );
                    })
                )}

                {isAnalyzing && (
                    <Box mt={3} ml={7} p={3} bgcolor="#f8fafc" borderRadius="20px" border="1px solid #e2e8f0" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                        <Box display="flex" alignItems="center" mb={2.5}>
                            <CircularProgress size={18} thickness={5} style={{ color: '#1e88e5', marginRight: 14 }} />
                            <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#1e293b', letterSpacing: '0.3px' }}>
                                {analysisProgress?.current_step || "Agent is processing your intelligence request..."}
                            </Typography>
                        </Box>
                        
                        {analysisProgress?.steps && analysisProgress.steps.length > 0 && (
                            <Box display="flex" flexDirection="column" gap={1.8}>
                                {analysisProgress.steps.map((step, idx) => {
                                    const isMajor = step.step && step.total;
                                    const isRunning = step.status === 'running';
                                    const isDone = step.status === 'done';
                                    
                                    return (
                                        <Box key={idx} display="flex" alignItems="flex-start" gap={1.8} style={{ opacity: isDone ? 0.7 : 1, transition: 'opacity 0.3s' }}>
                                            <Box mt={0.3} display="flex" justifyContent="center" width={20}>
                                                {isDone ? (
                                                    <CheckCircleIcon style={{ fontSize: 18, color: '#10b981' }} />
                                                ) : isRunning ? (
                                                    <Box 
                                                        className="step-spinner" 
                                                        style={{ 
                                                            width: 14, 
                                                            height: 14, 
                                                            borderRadius: '50%', 
                                                            border: '2.5px solid #3b82f6', 
                                                            borderTopColor: 'transparent'
                                                        }} 
                                                    />
                                                ) : (
                                                    <FiberManualRecordIcon style={{ fontSize: 10, color: '#cbd5e1', marginTop: 4 }} />
                                                )}
                                            </Box>
                                            <Box flex={1}>
                                                <Typography variant="body2" style={{ 
                                                    color: isDone ? '#64748b' : '#0f172a',
                                                    fontWeight: isRunning ? 700 : 500,
                                                    fontSize: isMajor ? '0.9rem' : '0.85rem',
                                                    lineHeight: 1.4
                                                }}>
                                                    {isMajor && <span style={{ color: '#3b82f6', marginRight: 6 }}>[{step.step}/{step.total}]</span>}
                                                    {step.message}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                        
                        {!analysisProgress?.steps && (
                            <Box display="flex" alignItems="center" mt={2}>
                                <span className="shiny-star" style={{ color: '#1e88e5', fontSize: 24, marginRight: 16 }}>✦</span>
                                <Typography variant="body2" style={{ fontStyle: 'italic', fontWeight: 600, color: '#1e88e5' }}>
                                    Initializing multi-agent protocols...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Elite Chat Input - Floating Architecture */}
            {showInput && (
                <Box
                    px={4}
                    pb={4}
                    pt={2}
                    bgcolor="transparent"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    style={{ position: 'relative' }}
                >
                    <Box width="100%" maxWidth="850px">
                        <Paper
                            elevation={0}
                            style={{
                                padding: '10px 14px 10px 24px',
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '24px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                                transition: 'all 0.3s ease',
                                transform: isAnalyzing ? 'translateY(2px)' : 'none',
                                opacity: isAnalyzing ? 0.7 : 1
                            }}
                            onFocus={(e) => {
                                e.currentTarget.style.boxShadow = '0 15px 45px -12px rgba(30, 136, 229, 0.15)';
                                e.currentTarget.style.borderColor = '#93c5fd';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(0,0,0,0.1)';
                                e.currentTarget.style.borderColor = '#e2e8f0';
                            }}
                        >
                            <TextField
                                fullWidth
                                multiline
                                maxRows={6}
                                placeholder="Consult the Agent on ESG performance or peer simulation..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyPress}
                                disabled={isAnalyzing}
                                InputProps={{
                                    disableUnderline: true,
                                    style: {
                                        fontSize: '15.5px',
                                        color: '#334155',
                                        fontWeight: 500,
                                        lineHeight: 1.5
                                    }
                                }}
                            />
                            <IconButton
                                onClick={handleSend}
                                disabled={!inputValue.trim() || isAnalyzing}
                                style={{
                                    backgroundColor: inputValue.trim() && !isAnalyzing ? '#1e293b' : '#f1f5f9',
                                    color: inputValue.trim() && !isAnalyzing ? '#fff' : '#94a3b8',
                                    marginLeft: '12px',
                                    width: '44px',
                                    height: '44px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: inputValue.trim() && !isAnalyzing ? '0 4px 12px rgba(0,0,0,0.2)' : 'none',
                                    transform: inputValue.trim() && !isAnalyzing ? 'scale(1.05)' : 'scale(1)'
                                }}
                            >
                                <SendIcon style={{ fontSize: '20px' }} />
                            </IconButton>
                        </Paper>
                        <Typography
                            variant="caption"
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                marginTop: '16px',
                                color: '#94a3b8',
                                letterSpacing: '0.3px',
                                fontWeight: 500
                            }}
                        >
                            Agent intelligence is powered by live indexed ESG datasets. Precision may vary by industry.
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ChatWorkspace;
